import { Link as RouterLink, useLocation } from 'react-router'

import LogoIcon, { useLogoOnLight } from '@icons/Logo'

import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'
import Typography from '@mui/material/Typography'

import { useMe } from '@shared/providers/src/MeProvider'

import useFormatMessage from '@hooks/useFormatMessage'
import { glassChrome, navCapsule } from '@utils/StylesHelper'
import Avatar from '@components/Avatar'

import NavBadge from '../NavBadge'
import { isNavItemActive, useNavigationItems } from '../navigation'

/** How far the panel floats from the edges of the window. Anything else floating in
 *  the expanded shell keeps the same distance, so they all line up. */
export const INSET = 12
/** The panel plus the gutters either side of it — what a page has to keep clear. */
export const SIDE_NAV_WIDTH = 264

/**
 * The list pane a two-column page keeps beside the navigation: conversations on
 * Messages, settings on Account. One number so the two screens line up.
 */
export const SIDE_PANE_WIDTH = 360

/**
 * Persistent side navigation for the expanded shell.
 *
 * A panel floating inside the window rather than a column walled off from it. Unlike
 * the bottom tab bar it never hides: a wide screen has room to keep the whole app
 * reachable while you read one lab result. Renders nothing inside the native shell.
 *
 * @returns {JSX.Element|null} The rendered navigation.
 */
export default function SideNav() {
  const me = useMe()
  const { pathname } = useLocation()
  const formatMessage = useFormatMessage()
  const items = useNavigationItems()
  const onLight = useLogoOnLight()

  if (window.isNativeApp === true) return null

  const name = [me.firstName, me.lastName].filter(Boolean).join(' ')

  return (
    <Box component="nav" aria-label={formatMessage('navigation.tabs.a11y.label')} sx={styles.root}>
      <Box sx={styles.brand(onLight)}>
        {/* Height only, so the width follows the artwork */}
        <LogoIcon onLight={onLight} height={34} />
      </Box>

      <Box sx={styles.items}>
        {items.map(({ id, to, label, a11yLabel, Icon, IconActive, count, badgeColor }) => {
          const active = isNavItemActive(pathname, to)
          const NavIcon = active ? IconActive : Icon

          return (
            <Box
              id={id}
              key={to}
              component={RouterLink}
              to={to}
              aria-current={active ? 'page' : undefined}
              aria-label={a11yLabel}
              data-testid={`side-nav-${label}`}
              sx={styles.item(active)}
            >
              <NavIcon fontSize="small" aria-hidden="true" />
              <Typography sx={styles.itemLabel}>{label}</Typography>
              <NavBadge count={count} color={badgeColor} sx={styles.badge} testId={`nav-badge-${label}`} />
            </Box>
          )
        })}
      </Box>

      <Box component={RouterLink} to="/app/account" sx={styles.user} data-testid="side-nav-user">
        <Avatar user={me} size="sm" variant="circular" />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 'bold' }}>
            {name}
          </Typography>
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
            {me.email}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

const styles = {
  root: (theme) => ({
    ...glassChrome(theme),
    position: 'fixed',
    top: INSET,
    bottom: INSET,
    left: INSET,
    width: SIDE_NAV_WIDTH - INSET * 2,
    zIndex: theme.zIndex.appBar,
    borderRadius: 3,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    p: 1,
  }),
  // A white-label logo is drawn for the dark app bar it was asked for, so it keeps a
  // dark plate here rather than disappearing into a pale panel. Ours has an ink
  // colourway and needs nothing behind it.
  brand: (onLight) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // The wordmark is a link that fills its box, so centring the box moves nothing and
    // has to reach the image inside it
    '& a': { justifyContent: 'center' },
    ...(onLight
      ? // Even room above and below: the panel's own 8px padding on top, and the 4px
        // the column puts between its children on the bottom
        { py: 1.5, mb: 0.5 }
      : {
          height: 48,
          mb: 1.5,
          px: 3,
          borderRadius: 2.5,
          backgroundColor: 'primary.main',
          backgroundImage: 'linear-gradient(180deg, rgba(255, 255, 255, 0.16), rgba(255, 255, 255, 0))',
        }),
  }),
  items: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    flexGrow: 1,
  },
  item: (active) => ({
    ...navCapsule(active),
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1.25,
    color: active ? 'secondary.main' : 'text.primary',
    backgroundColor: (theme) => (active ? alpha(theme.palette.secondary.main, 0.12) : 'transparent'),
  }),
  itemLabel: {
    flexGrow: 1,
    fontWeight: 'bold',
  },
  badge: { minWidth: 20, height: 20 },
  user: {
    ...navCapsule(false),
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 1.5,
    py: 1.25,
    color: 'text.primary',
  },
}
